import React from 'react';
import { AlertTriangle, Info, ShieldCheck, HardDrive, Archive, Trash2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../ui/alert-dialog';

export const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return '-';
  const abs = Math.abs(bytes);
  if (abs < 1024) return `${bytes} B`;
  if (abs < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (abs < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
};

export const formatDate = (iso, withTime = false) => {
  if (!iso) return '-';
  const opts = { day: '2-digit', month: 'short', year: 'numeric' };
  if (withTime) Object.assign(opts, { hour: '2-digit', minute: '2-digit' });
  return new Date(iso).toLocaleDateString('es-CO', opts);
};

const ESTILO_ALERTA = {
  alta: 'bg-red-50 border-red-200 text-red-800',
  media: 'bg-amber-50 border-amber-200 text-amber-800',
  info: 'bg-sky-50 border-sky-200 text-sky-800',
};

export const AlertasPanel = ({ alertas = [] }) => {
  if (!alertas.length) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="mantenimiento-sin-alertas">
        <ShieldCheck className="w-4 h-4 flex-shrink-0" /> Sin alertas: el espacio y los respaldos están en orden.
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="mantenimiento-alertas">
      {alertas.map((a, i) => (
        <li key={i} className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${ESTILO_ALERTA[a.nivel] || ESTILO_ALERTA.info}`} data-testid={`mantenimiento-alerta-${a.nivel}`}>
          {a.nivel === 'info' ? <Info className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
          <span>{a.mensaje}</span>
        </li>
      ))}
    </ul>
  );
};

export const RespaldosPanel = ({ respaldos = [], onCrear, creando }) => (
  <section className="bg-white rounded-xl shadow-sm p-5" data-testid="mantenimiento-respaldos">
    <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
      <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Archive className="w-5 h-5 text-emerald-600" />Respaldos</h2>
      <button onClick={onCrear} disabled={creando} className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-60" data-testid="mantenimiento-respaldar">
        {creando ? 'Respaldando…' : 'Respaldar ahora'}
      </button>
    </div>
    <p className="text-xs text-slate-500 mb-4">Automático el primer domingo de cada mes a las 8:00 p. m. Cada copia se verifica después de guardarse.</p>
    {respaldos.length === 0 ? (
      <p className="text-sm text-slate-500">Aún no hay respaldos.</p>
    ) : (
      <ul className="divide-y text-sm">
        {respaldos.map(r => (
          <li key={r.ruta} className="py-2 flex justify-between gap-2" data-testid="mantenimiento-respaldo-item">
            <span className="text-slate-700">{formatDate(r.fecha, true)} <span className="text-xs text-slate-400">· {r.origen === 'automatico' ? 'automático' : 'manual'}</span></span>
            <span className="text-slate-500 whitespace-nowrap">{formatBytes(r.bytes)} {r.verificado && <span className="text-emerald-600">· verificado</span>}</span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

export const TarifasInactivasCard = ({ tarifas, onLimpiar, limpiando }) => (
  <section className="bg-white rounded-xl shadow-sm p-5" data-testid="mantenimiento-tarifas-inactivas">
    <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-1"><HardDrive className="w-5 h-5 text-sky-600" />Tarifas desactivadas</h2>
    <p className="text-xs text-slate-500 mb-4">Versiones viejas del tarificador que quedan al importar uno nuevo. Ninguna cotización ni venta depende de ellas.</p>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-2xl font-bold text-slate-800" data-testid="mantenimiento-tarifas-cantidad">
        {(tarifas?.cantidad || 0).toLocaleString('es-CO')} <span className="text-sm font-normal text-slate-500">· {formatBytes(tarifas?.tamano_bytes || 0)}</span>
      </p>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button disabled={!tarifas?.cantidad || limpiando} className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-red-300 text-red-700 hover:bg-red-50 transition-colors disabled:opacity-40" data-testid="mantenimiento-limpiar-tarifas">
            <Trash2 className="w-4 h-4" />{limpiando ? 'Limpiando…' : 'Limpiar'}
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar las tarifas desactivadas?</AlertDialogTitle>
            <AlertDialogDescription>Se borrarán {tarifas?.cantidad} tarifas viejas. Las tarifas activas, cotizaciones y ventas no se tocan. Haz un respaldo antes si quieres conservarlas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mantenimiento-limpiar-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={onLimpiar} className="bg-red-600 hover:bg-red-700" data-testid="mantenimiento-limpiar-confirmar">Borrar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </section>
);
